/**
 * Presentation-scope projection for an integration node's result (bd 2yi).
 *
 * The flow runner stores each integration result under `scope.integration[id]`,
 * where operator-authored templates and the bedrock prompt read it. Spreading
 * the validator's full locked metadata there exposed unmapped raw upstream
 * fields (and any future sensitive field) to a `{{ scope.integration.<id>.* }}`
 * token. This projection exposes ONLY the operator-declared `outputMapping`
 * fields, plus structural `ok` and the locked amount — so a template can reach
 * exactly what the operator mapped, and nothing else.
 */

/** Structural shape of an integration result the runner receives. */
export interface IntegrationResultLike {
  ok: boolean;
  lockedFields?: {
    amount?: { value: unknown; currency: string };
    metadata?: Record<string, unknown>;
  };
  scopePatch?: { outputMapping?: Record<string, unknown> };
}

/**
 * Build the `scope.integration[id]` entry, exposing only operator-mapped
 * fields. The raw `lockedFields.metadata` is intentionally NOT spread — that
 * was the over-exposure (bd 2yi). The locked amount is surfaced as the
 * structural `amount_value` / `amount_currency` the confirm page reads.
 */
export function buildIntegrationScopeEntry(
  result: IntegrationResultLike,
): Record<string, unknown> {
  const amount = result.lockedFields?.amount;
  return {
    ok: result.ok,
    ...(amount
      ? { amount_value: String(amount.value), amount_currency: amount.currency }
      : {}),
    // Operator-declared mapped fields only. Spread last so a deliberate mapping
    // is authoritative.
    ...(result.scopePatch?.outputMapping ?? {}),
  };
}
